import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {SiteDto} from '../../objects/site.dto';
import {SiteInstallationStateEnum} from '../../objects/site-installation-state.enum';
import {PlannedSiteFilterModel} from '../../objects/planned-site-filter.model';
import {InstallationWeekFilterEnum} from '../../objects/installation-week-filter.enum';
import {ServiceTechnicianDtoModel} from "../../../../models/service-technician-dto.model";
import {User} from '../../../../models/user-model';
import {JuconnUserRoleEnum} from '../../../../models/juconn-user-role.enum';
import {DataService} from '../../../../services/data.service';
import {UserService} from '../../../../services/user.service';
import {StatusFilterEnum} from "../../objects/status-filter.enum";
import {CustomerDtoModel} from "../../../../models/customer-dto.model";

type PlannerStatus = {
  label: string;
  cssClass: string;
};

@Component({
  selector: 'app-planner-list',
  templateUrl: './planner-list.component.html',
  styleUrls: ['./planner-list.component.scss']
})
export class PlannerListComponent implements OnInit, OnChanges {
  @Input() public sites: SiteDto[] = [];
  @Input() public filter: PlannedSiteFilterModel;
  @Input() public customers: CustomerDtoModel[] = [];
  @Input() public serviceTechnicians: ServiceTechnicianDtoModel[] = [];
  @Input() public installationWeekFilterOptions: { value: InstallationWeekFilterEnum; label: string }[] = [];
  @Input() public statusOptions: { value: StatusFilterEnum; label: string }[] = [];
  @Output() public refreshRequested: EventEmitter<PlannedSiteFilterModel> = new EventEmitter<PlannedSiteFilterModel>();

  public showLegend = false;
  public assignmentMode = false;
  public sitesMarkedForAssignment: SiteDto[] = [];
  public assignmentInstallationPersonEmail: string;
  public assignmentInstallationWeek: number;
  public createSite: SiteDto;
  public selectedSite: SiteDto;
  public modalSiteDetail = {showModal: false};
  public modalCreateSiteDetail = {showModal: false};

  private user: User;

  public page = 1;
  public pageSize = 10;
  public readonly pageSizeOptions = [10, 25, 50];


  private columnNames = [
    {
      cellName: 'Status',
      getValue: e => this.getStatus(e).label
    },
    {
      cellName: 'Kunde',
      getValue: e => e.customerName?.replace('[IMMO] ', '') || '-'
    },
    {
      cellName: 'Adresse',
      getValue: e => e.street + ' ' + e.streetNumber + ', ' + e.city + ' ' + e.postCode + (e.additionalName ? (' · ' + e.additionalName) : '')
    },
    {
      cellName: 'Seriennummer',
      getValue: e => this.serialNumbers(e)
    },
    {
      cellName: 'Ansprechpartner',
      getValue: (e) => e.contactPersonName ? (e.contactPersonName + ' (' + (e.contactPersonNumber ? e.contactPersonNumber : '-') + ')') : '-'
    },
    {
      cellName: 'Servicetechniker',
      getValue: e => e.installationPersonEmail ? e.installationPersonEmail : 'nicht zugeordnet'
    },
    {
      cellName: 'KW',
      getValue: e => e.installationWeek ? 'KW ' + e.installationWeek : '-'
    },
    {
      cellName: 'Steuerung',
      getValue: e => this.installedControlLabel(e)
    },
  ];

  constructor(
    private userService: UserService,
    private dataService: DataService,
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.user = this.userService.user;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']?.currentValue) {
      this.filter = {...this.filter, ...changes['filter'].currentValue};
    }

    if (changes['sites']) {
      this.page = 1;
    }
    this.setPage(this.page);
  }

  public get totalPages(): number {
    return Math.max(1, Math.ceil(this.sites.length / this.pageSize));
  }

  public get pagedSites(): SiteDto[] {
    const start = (this.page - 1) * this.pageSize;
    return this.sites.slice(start, start + this.pageSize);
  }

  public get pageFrom(): number {
    return this.sites.length ? ((this.page - 1) * this.pageSize) + 1 : 0;
  }

  public get pageTo(): number {
    return Math.min(this.page * this.pageSize, this.sites.length);
  }

  public get pageNumbers(): number[] {
    return Array.from({length: this.totalPages}, (_, i) => i + 1);
  }

  public get isFirstPage(): boolean {
    return this.page === 1;
  }

  public get isLastPage(): boolean {
    return this.page === this.totalPages;
  }

  public get filteredCount(): number {
    return this.sites.length;
  }

  public setPage(page: number): void {
    this.page = Math.min(Math.max(page, 1), this.totalPages);
  }

  public onPageSizeChange(value: string): void {
    this.pageSize = Number(value) || 10;
    this.page = 1;
  }

  public goToFirstPage(): void {
    this.setPage(1);
  }

  public goToLastPage(): void {
    this.setPage(this.totalPages);
  }

  public goToNextPage(): void {
    this.setPage(this.page + 1);
  }

  public goToPreviousPage(): void {
    this.setPage(this.page - 1);
  }

  public onFiltersChange(): void {
    this.page = 1;
    this.setPage(1);
    this.refreshRequested.emit({...this.filter});
  }

  public trackBySite(index: number, site: SiteDto): number {
    return site.id ?? index;
  }

  public getStatus(site: SiteDto): PlannerStatus {
    if (site.note) {
      return {label: 'Kommentar', cssClass: 'kommentar'};
    }

    if (site.siteInstallationState === SiteInstallationStateEnum.ONGOING) {
      return {label: 'Montiert', cssClass: 'gateway'};
    }

    if (site.siteInstallationState === SiteInstallationStateEnum.PLANNED) {
      const planned = !!site.installationWeek && !!site.installationPersonEmail;
      return planned
        ? {label: 'Geplant', cssClass: 'geplant'}
        : {label: 'Ungeplant', cssClass: 'ungeplant'};
    }

    return {label: 'Abgeschlossen', cssClass: 'fertig'};
  }

  public serialNumbers(site: SiteDto): string {
    return site.serialNumbers?.length ? site.serialNumbers.join(', ') : '–';
  }

  public installedControlLabel(site: SiteDto): 'Ja' | 'Nein' {
    const normalized = (site.installedControl || '').trim().toLowerCase();
    return ['true', '1', 'yes', 'ja'].includes(normalized) ? 'Ja' : 'Nein';
  }

  public isPlanner(): boolean {
    return !!(this.user?.roles?.includes(JuconnUserRoleEnum.user_planner));
  }

  public isPlannerAndIsACustomerSelected(): boolean {
    return this.isPlanner() && this.filter?.jucCustomerId !== -1 && this.filter?.jucCustomerId !== undefined;
  }

  public enableAssignmentMode(): void {
    this.filter.installationWeekFilter = InstallationWeekFilterEnum.OPENED;
    this.assignmentInstallationPersonEmail = null;
    this.assignmentInstallationWeek = null;
    this.sitesMarkedForAssignment = [];
    this.assignmentMode = true;
    this.page = 1;
    this.setPage(1);
    this.refreshRequested.emit({...this.filter});
  }


  public toggleSiteAssignment(site: SiteDto): void {
    if (this.isSiteMarkedForAssignment(site)) {
      this.sitesMarkedForAssignment = this.sitesMarkedForAssignment.filter(s => s !== site);
    } else {
      this.sitesMarkedForAssignment.push(site);
    }
  }

  public isSiteMarkedForAssignment(site: SiteDto): boolean {
    return this.sitesMarkedForAssignment.includes(site);
  }

  public isAnySiteMarkedForAssignment(): boolean {
    return this.filter.jucCustomerId !== -1 && this.isPlanner() && this.sitesMarkedForAssignment.length > 0;
  }

  public canSitesMarkedForAssignmentBeSaved(): boolean {
    return (!!this.assignmentInstallationPersonEmail || (this.assignmentInstallationWeek !== null && this.assignmentInstallationWeek !== undefined)) && this.sitesMarkedForAssignment.length > 0;
  }

  public async updateMarkedSites(): Promise<void> {
    if (this.canSitesMarkedForAssignmentBeSaved()) {
      const sitesToUpdate: SiteDto[] = [];

      this.sitesMarkedForAssignment.forEach(s => {
        const site = {...s};
        if (this.assignmentInstallationPersonEmail && this.assignmentInstallationPersonEmail != null) {
          site.installationPersonEmail = this.assignmentInstallationPersonEmail;
        }
        if (this.assignmentInstallationWeek && this.assignmentInstallationWeek != null) {
          site.installationWeek = this.assignmentInstallationWeek;
        }
        sitesToUpdate.push(site);
      });

      await this.dataService.updatePlannedSites(sitesToUpdate);
      this.cancelAssigment();
      this.refreshRequested.emit({...this.filter});
    }
  }

  public cancelAssigment(): void {
    this.sitesMarkedForAssignment = [];
    this.assignmentInstallationPersonEmail = null;
    this.assignmentInstallationWeek = null;
    this.assignmentMode = false;
  }

  public cancelAssignment(): void {
    this.cancelAssigment();
  }

  public openCreateSiteModal(): void {
    this.createSite = {
      city: null,
      street: null,
      streetNumber: null,
      additionalName: null,
      postCode: null,
      contactPersonName: null,
      contactPersonNumber: null,
      customerId: this.filter?.jucCustomerId,
      customerName: null,
      installationWeek: null,
      installationPersonEmail: null,
      siteInstallationState: SiteInstallationStateEnum.PLANNED,
      latitude: null,
      longitude: null,
      salesforceId: null,
      serialNumbers: [],
      installedControl: null,
      note: null
    };
    this.modalCreateSiteDetail.showModal = true;
  }

  public onSiteUpdated(): void {
    this.onFiltersChange();
  }

  public showDetails(site: SiteDto): void {
    if (this.assignmentMode) {
      this.toggleSiteAssignment(site);
    } else {
      this.selectedSite = site;
      this.modalSiteDetail.showModal = true;
    }
  }

  public exportData() {
    const sites = this.sites;
    let csv = this.columnNames.map((c) => c.cellName).join(';') + '\r\n';
    for (const site of sites) {
      for (let i = 0; i < this.columnNames.length; i++) {
        csv += this.columnNames[i].getValue(site);
        csv += i + 1 === this.columnNames.length ? '\r\n' : ';';
      }
    }
    const universalBOM = '\uFEFF';
    const link = document.createElement('a');
    link.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(universalBOM + csv));
    link.setAttribute('download', 'sites.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
